<template>
  <div>
    <AisStats>
      <template #default="{ nbHits, query }">
        <div>
          <template v-if="query">
            <strong>{{ nbHits }}</strong> records found
            for <strong><q>{{ query }}</q></strong>
          </template>
        </div>
      </template>
    </AisStats>
    <AisCurrentRefinements>
      <template #default="{ items, createURL }">
        <div
          v-if="items.length"
          class="inline-flex items-baseline"
        >
          <strong class="text-sm font-bold mr-4">Showing Results for:</strong>
          <ul
            class="flex gap-1.5 flex-wrap-reverse"
            :class="{ 'mt-5': items.length }"
          >
            <template
              v-for="item in items"
              :key="item.attribute"
            >
              <li
                v-for="refinement in item.refinements"
                :key="[
                  refinement.attribute,
                  refinement.type,
                  refinement.value,
                  refinement.operator
                ].join(':')"
                class="shrink-0"
              >
                <a
                  :href="createURL(refinement)"
                  class="flex items-center border border-gray-blue-200 bg-white py-1 px-2.5 rounded-full text-sm font-medium hover:bg-blue-200 hover:text-blue-800"
                  @click.prevent="item.refine(refinement)"
                >
                  <span class="mr-px capitalize sr-only">{{ item.label }}:</span>
                  <span class="mr-1">{{ searchStore.dictionaryLookup(refinement.label) }}</span>
                  <SvgIcon
                    aria-hidden="true"
                    icon-name="close"
                    class="w-2.5 h-2.5 shrink-0 text-gray-blue-300"
                  />
                </a>
              </li>
            </template>
          </ul>
        </div>
      </template>
    </AisCurrentRefinements>
  </div>
  <div
    v-if="showSortControls"
    class="flex gap-2.5 items-center shrink-0"
  >
    <span class="font-bold">Sort By:</span>
    <AisSortBy
      :items="getSortByOptionsBySearchType(props.searchType, props.indexPrefix)"
      :transform-items="filterSortByItems"
      :class-names="{
        'ais-SortBy-select': 'min-w-[200px]'
      }"
    />
  </div>
</template>

<script lang="ts">
export default { name: 'FilterSummary' }
</script>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { getSortByOptionsBySearchType, handleToIndexName } from '~/components/search/helpers/indexHelper'

import {
  AisStats,
  AisCurrentRefinements,
  AisSortBy,
} from 'vue-instantsearch/vue3/es'

import SvgIcon from '../../global/SvgIcon.vue'
import { useSearchStore } from '~/store/modules/search'

import { SearchType } from '~/components/search/enums/SearchType'
import { AlgoliaIndices } from '~/components/search/enums/AlgoliaIndices'
import { SortByOption } from '~/types/SortByOption'

const props = defineProps<{
  indexPrefix: string,
  searchType: SearchType,
  indexName: string,
}>()

const searchStore = useSearchStore()

const { query } = storeToRefs(searchStore)

const filterSortByItems = (items: SortByOption[]) => {
  if (query.value.length) {
    // don't show the default sort option when searching by keyword
    return items.filter(item => item.value !== props.indexName)
  }

  // else, hide "best match" option when not searching by keyword
  return items.filter(item => item.value !== handleToIndexName(props.indexPrefix, AlgoliaIndices.PRIMARY))
}

const showSortControls = [SearchType.PRODUCT].includes(props.searchType)
</script>
